/**
 * Data-driven keybinding engine.
 * Replaces the old hard-coded useKeyboardShortcuts hook.
 *
 * Reads keybinding definitions from the store and dispatches
 * matching keyboard/mouse events to action handlers.
 */

import { useEffect, useRef } from 'react';
import { useEditorStore, stepsForType } from '../store/editorStore';
import type { ToolMode } from '../types/beatmap';
import {
  eventToKey,
  eventToMods,
  findMatchingAction,
  synthVrBindings,
  VR_ONLY_BINDINGS,
  VR_ONLY_ACTIONS,
} from '../config/keybindings';
import { playStepChangeTick } from '../audio/tickSound';
import type { WallKind } from '../components/three/tunnelToolModes';
import { selectToolAvailable, isSynthVrView } from '../store/editorSettings';
import { stepFrom } from '../store/stepGrid';

/** Index of the value in `opts` closest to `step` — tolerant of ternary steps
 *  that aren't an exact array member (indexOf would return -1). */
function nearestStepIndex(opts: number[], step: number): number {
  let best = 0, bestDist = Infinity;
  for (let i = 0; i < opts.length; i++) {
    const d = Math.abs(opts[i] - step);
    if (d < bestDist) { bestDist = d; best = i; }
  }
  return best;
}

/** The playback speeds the Speed buttons in the sidebar step through. */
const PLAYBACK_SPEEDS = [0.25, 0.5, 1, 2];

/** One step along `PLAYBACK_SPEEDS` from the current speed, stopping at either end. */
function stepPlaybackSpeed(dir: 1 | -1): void {
  const s = useEditorStore.getState();
  const next = nearestStepIndex(PLAYBACK_SPEEDS, s.playbackSpeed) + dir;
  if (next >= 0 && next < PLAYBACK_SPEEDS.length) s.setPlaybackSpeed(PLAYBACK_SPEEDS[next]);
}

// The beat playback last started from, whichever control started it — R returns here on pause. Module
// level because buildActionHandlers runs on every render of the hook; this subscribes exactly once.
let playStartBeat: number | null = null;
useEditorStore.subscribe((s, prev) => {
  if (s.playback.playing && !prev.playback.playing) playStartBeat = prev.playback.currentBeat;
});

/**
 * Build a map of action ID → handler function.
 * Called once and memoized; handlers read state imperatively via getState().
 */
function buildActionHandlers(): Record<string, () => void> {
  const gs = () => useEditorStore.getState();
  // A wall hotkey does what the Obstacles panel's button does: in Synth VR it toggles or replaces the
  // one wall on the playhead's beat (SRVR-0313); every other view keeps appending at the playhead.
  const wall = (kind: WallKind) => (gs().activeView === 'synth-rider' ? gs().toggleWallAtPlayhead(kind) : gs().placeWallAtPlayhead(kind));
  // Area selection belongs to the Synth VR editor only; the Console editor shares this engine and keeps
  // its index-based editing keys exactly as they were.
  const vr = () => gs().activeView === 'synth-rider';
  // In Synth VR the editing keys act on the area range when one is set, and on the playhead's beat when
  // nothing at all is selected (SRVR-0319..0321). Only a click or marquee selection keeps the index actions.
  const byArea = () => { const s = gs(); return vr() && (!!(s.areaRange || s.areaGesture) || (s.selectedNoteIds.length === 0 && s.selectedSlideIds.length === 0)); };

  return {
    // ── Tool Selection ─────────────────────────────────────────
    select_tool:             () => { if (selectToolAvailable(gs().activeView)) gs().setToolMode('select'); },
    eraser_tool:             () => gs().setToolMode('eraser'),
    select_left_hand:        () => gs().setToolMode('note_left'),
    select_right_hand:       () => gs().setToolMode('note_right'),
    select_onehand_special:  () => gs().setToolMode('note_onehand'),
    select_twohand_special:  () => gs().setToolMode('note_bothhands'),

    // ── Playback ───────────────────────────────────────────────
    toggle_play:         () => gs().setPlaying(!gs().playback.playing),
    toggle_play_return:  () => {
      const s = gs();
      if (s.playback.playing) {
        s.setPlaying(false);
        // Synth VR: pausing with R goes back to the beat playback started from. Console keeps pausing in place.
        if (vr() && playStartBeat !== null) gs().setCurrentBeat(playStartBeat);
      } else {
        s.setPlaying(true);
      }
    },

    // ── Selection ──────────────────────────────────────────────
    select_all:          () => gs().selectAll(),
    delete_selected:     () => {
      const s = gs();
      // While the Shapes panel is open, Delete removes the selected decor
      // group(s) (click- or box-selected) instead of notes.
      if (s.shapeDesignerOpen && s.selectedDesignShapeIds.length) {
        s.removeDesignShapes(s.selectedDesignShapeIds);
        return;
      }
      if (byArea()) { s.areaDelete(); return; }
      // A click or marquee selection: its notes AND obstacles together.
      const noteIds = s.selectedNoteIds;
      const slideIds = s.selectedSlideIds;
      if (slideIds.length > 0) s.removeSlides([...slideIds]);
      if (noteIds.length > 0) s.removeNotes([...noteIds]);
      // Outside Synth VR, nothing selected → Delete removes the note under the cursor (reviewer ask:
      // DEL should work at the editor plane without area-selecting first).
      if (noteIds.length === 0 && slideIds.length === 0 && s.hoveredNoteIndex !== null) {
        s.removeNotes([s.hoveredNoteIndex]);
      }
    },

    // ── Clipboard ──────────────────────────────────────────────
    // While the Shapes panel is open it OWNS clipboard/undo so you can
    // copy/paste/undo decor groups; otherwise these operate on notes as usual.
    copy:                () => {
      const s = gs();
      if (s.shapeDesignerOpen && s.selectedDesignShapeIds.length) s.copyDesignShape();
      else if (byArea()) s.areaCopy();
      else s.copySelected();
    },
    cut:                 () => (byArea() ? gs().areaCut() : gs().cutSelected()),
    paste:               () => {
      const s = gs();
      if (s.shapeDesignerOpen && s.designClipboard) s.pasteDesignShape();
      else if (vr() && s.areaClipboard) s.areaPaste(false);
      else s.pasteAtBeat(s.playback.currentBeat);
    },
    paste_mirror:        () => {
      const s = gs();
      if (vr() && s.areaClipboard) s.areaPaste(true);
      else s.pasteMirrorAtBeat(s.playback.currentBeat);
    },

    // ── Undo/Redo ──────────────────────────────────────────────
    undo:                () => {
      const s = gs();
      if (s.shapeDesignerOpen && s.designUndoStack.length) s.undoDesign();
      else s.undo();
    },
    redo:                () => {
      const s = gs();
      if (s.shapeDesignerOpen && s.designRedoStack.length) s.redoDesign();
      else s.redo();
    },

    // ── Note Editing ───────────────────────────────────────────
    // The index flips move notes only, so a lone obstacle selection flips the playhead's beat instead.
    flip_object:         () => (byArea() || (vr() && gs().selectedNoteIds.length === 0) ? gs().areaFlip('x') : gs().mirrorSelected()),
    flip_object_y:       () => (byArea() || (vr() && gs().selectedNoteIds.length === 0) ? gs().areaFlip('y') : gs().flipSelectedY()),
    duplicate_opposite:  () => gs().duplicateSelectedOppositeHand(),
    scale_selection_expand:   () => { if (!vr()) gs().scaleSelection(2); },     // Synth VR has no Scale Selection
    scale_selection_compress: () => { if (!vr()) gs().scaleSelection(0.5); },
    toggle_mirror_mode:  () => gs().toggleMirrorMode(),
    mirror_invert_y:     () => gs().toggleMirrorInvertY(),
    join_notes_rail:     () => gs().joinNotesToRail(),
    split_rail:          () => gs().splitRailAtNode(),
    recalc_rail:         () => gs().recalcRailNodesByStep(),
    wall_left:           () => wall('wall_left'),
    wall_right:          () => wall('wall_right'),
    wall_center:         () => wall('center'),
    wall_diag_left:      () => wall('angle_left'),
    wall_diag_right:     () => wall('angle_right'),
    wall_crouch:         () => wall('crouch'),
    wall_square:         () => wall('square'),
    wall_triangle:       () => wall('triangle'),
    add_note_playing:    () => { if (!vr()) gs().placeNoteAtPlayhead(); },   // Synth VR has no Add Note at Playhead
    toggle_same_note_overwrite: () => gs().toggleSameNoteOverwrite(),
    snip_rail:           () => gs().snipRailAtPlayhead(),
    cycle_note_type:     () => { if (!vr()) gs().cycleSelectedNoteType(); },   // Synth VR has no Cycle Note Type (Shift + click cycles colour)
    snap_selection_to_rail: () => gs().snapSelectionToRail(),
    delete_all:          () => { if (!vr()) gs().deleteAllObjects(); },   // Synth VR has no Delete All
    clear_bookmarks:     () => gs().requestClearBookmarks(),   // Synth VR asks first: bookmarks are not in the history
    toggle_long_note:    () => {
      const s = gs();
      const mode = s.toolMode;
      // Toggle between note and rail mode for the current hand
      const toggleMap: Record<string, ToolMode> = {
        note_left: 'rail_left', rail_left: 'note_left',
        note_right: 'rail_right', rail_right: 'note_right',
        note_onehand: 'rail_onehand', rail_onehand: 'note_onehand',
        note_bothhands: 'rail_bothhands', rail_bothhands: 'note_bothhands',
      };
      if (toggleMap[mode]) s.setToolMode(toggleMap[mode]);
    },

    // ── Timeline ───────────────────────────────────────────────
    move_forward:        () => {
      const s = gs();
      const step = s.scrollStep;
      const newBeat = s.playback.playing ? s.playback.currentBeat + step : stepFrom(s.activeView, s.playback.currentBeat, step, 1);
      const bpm = s.beatmap?.bpm || 120;
      const offset = s.beatmap?.offset || 0;
      const time = (newBeat / (bpm / 60)) + (offset / 1000);
      s.setCurrentTime(Math.max(0, time));
    },
    move_backward:       () => {
      const s = gs();
      const step = s.scrollStep;
      const newBeat = s.playback.playing ? s.playback.currentBeat - step : stepFrom(s.activeView, s.playback.currentBeat, step, -1);
      const bpm = s.beatmap?.bpm || 120;
      const offset = s.beatmap?.offset || 0;
      const time = (newBeat / (bpm / 60)) + (offset / 1000);
      s.setCurrentTime(Math.max(0, time));
    },
    timeline_start:      () => gs().setCurrentTime(0),
    timeline_end:        () => gs().setCurrentTime(gs().playback.duration),
    // The same stores actions and speed steps the sidebar's buttons use; Synth VR only, Console is as it was.
    jump_next_bookmark:  () => { if (vr()) gs().jumpToNextBookmark(); },
    jump_prev_bookmark:  () => { if (vr()) gs().jumpToPrevBookmark(); },
    playback_speed_down: () => { if (vr()) stepPlaybackSpeed(-1); },
    playback_speed_up:   () => { if (vr()) stepPlaybackSpeed(1); },

    // ── Beat Step ──────────────────────────────────────────────
    beat_step_down:      () => {
      const opts = stepsForType(gs().stepType);
      const idx = nearestStepIndex(opts, gs().scrollStep);
      if (idx > 0) {
        gs().setScrollStep(opts[idx - 1]);
        playStepChangeTick('down');
      }
    },
    beat_step_up:        () => {
      const opts = stepsForType(gs().stepType);
      const idx = nearestStepIndex(opts, gs().scrollStep);
      if (idx < opts.length - 1) {
        gs().setScrollStep(opts[idx + 1]);
        playStepChangeTick('up');
      }
    },
    cycle_step_type:     () => gs().cycleStepType(),

    // ── Interface ──────────────────────────────────────────────
    open_settings:       () => gs().setSettingsOpen(!gs().settingsOpen),
    open_file_manager:   () => gs().setFileManagerOpen(!gs().fileManagerOpen),
    new_map:             () => gs().setNewMapOpen(!gs().newMapOpen),
    toggle_keybindings_panel: () => gs().setKeybindingsOpen(!gs().keybindingsOpen),
    toggle_grid_guide:   () => gs().setGridGuideVisible(!gs().gridGuideVisible),
    cycle_grid_guide:    () => {
      const types: Array<'solid' | 'outline' | 'circles'> = ['solid', 'outline', 'circles'];
      const current = gs().gridGuideType;
      const idx = types.indexOf(current);
      gs().setGridGuideType(types[(idx + 1) % types.length]);
    },
    toggle_snap_to_grid: () => gs().setSnapToGrid(!gs().snapToGrid),
    toggle_snap_to_rail: () => { if (isSynthVrView(gs().activeView)) gs().setRailSnapping(!gs().railSnapping); },
    toggle_sidebars:     () => {
      const s = gs();
      const allOpen = s.leftSidebarOpen && s.rightSidebarOpen;
      s.setLeftSidebarOpen(!allOpen);
      s.setRightSidebarOpen(!allOpen);
    },
    toggle_metronome:    () => gs().setMetronomeEnabled(!gs().metronomeEnabled),
    // Off → 1 → 2 → 3 → Off (SRVR-0314 AC4).
    toggle_highlighting: () => gs().setHighlightLevel(gs().highlightLevel + 1),

    // ── Camera ─────────────────────────────────────────────────
    camera_center:       () => gs().setCameraView('default'),
    camera_left:         () => gs().setCameraView('left'),
    camera_right:        () => gs().setCameraView('right'),
    camera_free:         () => gs().setCameraView('free'),

    // ── Volume ─────────────────────────────────────────────────
    decrease_music_vol:  () => gs().setMusicVolume(Math.max(0, gs().musicVolume - 0.05)),
    increase_music_vol:  () => gs().setMusicVolume(Math.min(1, gs().musicVolume + 0.05)),
    decrease_sfx_vol:    () => gs().setSfxVolume(Math.max(0, gs().sfxVolume - 0.05)),
    increase_sfx_vol:    () => gs().setSfxVolume(Math.min(1, gs().sfxVolume + 0.05)),

    // ── Save ───────────────────────────────────────────────────
    save_map:            () => {
      // Trigger the download/save flow — dispatch a custom event
      window.dispatchEvent(new CustomEvent('abm-save-map'));
    },

    // ── Bookmarks / Flash ────────────────────────────────────────
    // The same actions as the Light and Bookmark buttons (SRVR-0324).
    toggle_bookmark:     () => gs().toggleBookmarkAtPlayhead(),
    toggle_flash:        () => gs().toggleLightAtPlayhead(),

    // ── Area selection ─────────────────────────────────────────
    // The key is HELD while the playhead steps (SRVR-0315); its release is handled in handleKeyUp.
    area_selection:      () => { if (vr()) gs().areaKeyDown(); },
    area_selection_extend: () => { if (vr()) gs().areaExtendOrRecall(); },
    cycle_paste_behavior: () => { if (vr()) gs().cyclePasteMode(); },

    // ── These are mouse-action keybindings handled by the 3D scene, not here:
    // place_note, drag_snap_object, snap_grid_to_object, remote_delete,
    // rotate_wall_cw, rotate_wall_ccw, add_node_to_rail, change_note_color
  };
}

/** The ids the keyboard dispatcher has a handler for (the scene's mouse gestures and the wall-rotation keys are
 *  read elsewhere, through `gestures.ts`). A test sets this against the Keys list so no row lacks an actor. */
export function engineActionIds(): string[] {
  return Object.keys(buildActionHandlers());
}

// ── Main Hook ────────────────────────────────────────────────────────────────

export default function useKeybindingEngine() {
  const handlersRef = useRef<Record<string, () => void>>(buildActionHandlers());

  useEffect(() => {
    // ── Ctrl + mouse wheel → cycle step size ───────
    const handleWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const st = useEditorStore.getState();
      const opts = stepsForType(st.stepType);
      const idx = nearestStepIndex(opts, st.scrollStep);
      // The step arrays run fine → coarse, so a FINER grid is a LOWER index.
      // Scrolling up must subdivide (1/4 → 1/8), matching the desktop editor and
      // the usual zoom-in convention; it used to do the opposite.
      const dir = e.deltaY < 0 ? -1 : 1;
      const next = idx + dir;
      if (next >= 0 && next < opts.length) {
        st.setScrollStep(opts[next]);
        // Tick follows the gesture: scrolling up reads as "up".
        playStepChangeTick(dir < 0 ? 'up' : 'down');
      }
    };

    const handleKey = (e: KeyboardEvent) => {
      // Suppress shortcuts ONLY while the user is genuinely typing TEXT — a text
      // <input>, a <textarea>, or a contentEditable region. Everything else that
      // can hold focus but doesn't take typed text (a <select> dropdown, any
      // <button>, colour/checkbox/radio/range inputs — all over the Shape Designer)
      // must NOT swallow shortcuts: otherwise, once one keeps focus after a click,
      // Space (play/pause) and the rest stay dead until you click empty space.
      // For those focused controls we still fire the shortcut but preventDefault
      // below so Space doesn't ALSO activate the focused button / open the select.
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      // A numeric slider/stepper readout (marked data-num-readout): let plain
      // keys TYPE normally, but still fire Ctrl/Cmd shortcuts (undo/redo/copy/
      // paste) — otherwise, once a readout keeps focus, Ctrl+Z can't undo the
      // decor edit until you click away.
      const isNumReadout = tag === 'INPUT' && (target as HTMLInputElement).dataset?.numReadout != null;
      if (isNumReadout) {
        if (!(e.ctrlKey || e.metaKey)) return;   // plain key → type it, no shortcut
        // Ctrl/Cmd combo → fall through to shortcut matching (Ctrl+Z etc.)
      } else {
        // Suppress shortcuts while genuinely typing TEXT — a text <input>, a
        // <textarea>, or contentEditable. Everything else that can hold focus but
        // doesn't take typed text (a <select>, any <button>, colour/checkbox/range
        // inputs — all over the Shape Designer) must NOT swallow shortcuts.
        const isTyping =
          tag === 'TEXTAREA' ||
          !!target?.isContentEditable ||
          (tag === 'INPUT' &&
            !['color', 'checkbox', 'radio', 'range', 'button', 'submit', 'reset', 'file', 'image'].includes(
              (target as HTMLInputElement).type));
        if (isTyping) return;
      }

      // Suppress WASDQE when fly mode is active
      if (useEditorStore.getState().isFlyModeActive) {
        const k = e.key.toUpperCase();
        if (['W', 'A', 'S', 'D', 'Q', 'E'].includes(k)) return;
      }

      // If the keybindings rebind dialog is listening, don't dispatch
      if (useEditorStore.getState().keybindingsOpen) {
        // Only allow Escape to close the keybindings panel
        if (e.key === 'Escape') {
          useEditorStore.getState().setKeybindingsOpen(false);
          return;
        }
      }

      // ── Drag in progress: Escape cancels ─────────────────────
      if (useEditorStore.getState().draggingNoteIndex !== null) {
        if (e.key === 'Escape') {
          e.preventDefault();
          useEditorStore.getState().cancelDrag();
          return;
        }
      }

      // ── Rail placement: Enter finishes, Escape cancels ──────
      if (useEditorStore.getState().railPlacementActive) {
        if (e.key === 'Enter') {
          e.preventDefault();
          useEditorStore.getState().finishRailPlacement();
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          useEditorStore.getState().cancelRailPlacement();
          return;
        }
      }

      // ── Area range: Escape clears it (SRVR-0316 AC5) ────────
      if (e.key === 'Escape') {
        const st = useEditorStore.getState();
        if (st.activeView === 'synth-rider' && (st.areaRange || st.areaGesture)) {
          e.preventDefault();
          st.clearAreaRange();
          return;
        }
      }

      // Convert event to our normalized key + mods
      const key = eventToKey(e);
      const mods = eventToMods(e);

      // Find matching action
      const state = useEditorStore.getState();
      const bindings = state.activeView === 'synth-rider'
        ? synthVrBindings(state.keybindings)
        : state.keybindings.filter(b => !VR_ONLY_BINDINGS.has(b.id));   // Console never had these rows
      const actionId = findMatchingAction(bindings, key, mods);

      // The four actions only Synth VR acts on keep their rows here, so a key bound to one of them still
      // wins the match and does nothing, as before; Console neither acts on it nor prevents the default.
      if (actionId && state.activeView !== 'synth-rider' && VR_ONLY_ACTIONS.has(actionId)) return;

      // Holding the area-selection key auto-repeats its keydown; only the first press starts the
      // gesture (SRVR-0315 AC8).
      if (actionId === 'area_selection' && e.repeat) { e.preventDefault(); return; }
      // Held Ctrl+D auto-repeats too; it duplicates once, then the mouse repositions the copies.
      if (actionId === 'duplicate_opposite' && e.repeat) { e.preventDefault(); return; }

      if (actionId) {
        const handler = handlersRef.current[actionId];
        if (handler) {
          e.preventDefault();
          handler();
        }
      }
    };

    // ── Ctrl hold → temporary select mode ──────────────────────
    let toolBeforeCtrl: ToolMode | null = null;

    const handleKeyUp = (e: KeyboardEvent) => {
      // The area-selection key's release ends the gesture. Matched on the bound key alone: the
      // modifiers held at release need not be the ones held at the press.
      const st = useEditorStore.getState();
      if (st.areaGesture) {
        const b = st.keybindings.find(kb => kb.id === 'area_selection');
        const key = eventToKey(e);
        if (b && (b.primary.key === key || b.secondary.key === key)) st.areaKeyUp();
      }
      // Releasing the duplicate key, or a modifier of its combo, ends the reposition (Track.cs: the
      // combo no longer held).
      if (st.duplicateMoveActive) {
        const b = st.keybindings.find(kb => kb.id === 'duplicate_opposite');
        const key = eventToKey(e);
        const mod = e.key === 'Control' || e.key === 'Meta' || e.key === 'Shift' || e.key === 'Alt';
        if (mod || (b && (b.primary.key === key || b.secondary.key === key))) st.duplicateMoveEnd();
      }
      if ((e.key === 'Control' || e.key === 'Meta') && toolBeforeCtrl !== null) {
        // Restore previous tool — temporary switch so don't finish rail placement
        useEditorStore.getState().setToolMode(toolBeforeCtrl, true);
        toolBeforeCtrl = null;
      }
    };

    const handleKeyDownWithCtrl = (e: KeyboardEvent) => {
      // Ctrl press (alone, not as part of a combo with another key)
      if ((e.key === 'Control' || e.key === 'Meta') && toolBeforeCtrl === null) {
        const store = useEditorStore.getState();
        if (store.toolMode !== 'select') {
          toolBeforeCtrl = store.toolMode;
          // Temporary switch — don't finish rail placement
          store.setToolMode('select', true);
        }
      }
    };

    // A tool picked while Ctrl is held (Note Fill's Ctrl + click) is the one to restore at release.
    const handleCtrlToolPicked = (e: Event) => {
      if (toolBeforeCtrl !== null) toolBeforeCtrl = (e as CustomEvent<ToolMode>).detail;
    };
    window.addEventListener('abm-ctrl-tool-picked', handleCtrlToolPicked);
    window.addEventListener('keydown', handleKey);
    window.addEventListener('keydown', handleKeyDownWithCtrl);
    window.addEventListener('keyup', handleKeyUp);
    // The held duplicate key's mouse movement repositions the copies; the store ignores it otherwise.
    const handleDuplicateMove = (e: PointerEvent) => {
      const st = useEditorStore.getState();
      if (st.duplicateMoveActive) st.duplicateMoveBy(e.movementX, e.movementY);
    };
    // Focus lost while held: the keyup never arrives.
    const handleBlur = () => useEditorStore.getState().duplicateMoveEnd();
    window.addEventListener('pointermove', handleDuplicateMove);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      window.removeEventListener('pointermove', handleDuplicateMove);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('keydown', handleKeyDownWithCtrl);
      window.removeEventListener('abm-ctrl-tool-picked', handleCtrlToolPicked);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('wheel', handleWheel);
    };
  }, []);
}
